import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollText, Search, Trash2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function relativeTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172800) return "yesterday";
  return `${Math.floor(diff / 86400)} days ago`;
}

const actionTone = (a) => {
  if (a.startsWith("Deleted") || a.startsWith("Cleared")) return "bg-[var(--maroon)] text-[#FBF7DE]";
  if (a.startsWith("Created")) return "bg-[var(--emerald)] text-[#FBF7DE]";
  if (a.startsWith("Logged")) return "bg-[#F3EECB] text-[var(--olive)]";
  return "bg-[var(--olive)] text-[#FBF7DE]";
};

export default function AdminActivity() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ admins: [], actions: [] });
  const [adminFilter, setAdminFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user && !user.is_super) navigate("/admin");
  }, [user, authLoading, navigate]);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (adminFilter !== "all") params.set("admin_email", adminFilter);
    if (actionFilter !== "all") params.set("action", actionFilter);
    if (search.trim()) params.set("search", search.trim());
    api.get(`/admin/activity-logs?${params.toString()}`).then(({ data }) => { setLogs(data); setLoading(false); });
  };

  const loadMeta = () => api.get("/admin/activity-logs/meta").then(({ data }) => setMeta(data));

  useEffect(() => { if (user?.is_super) loadMeta(); }, [user]);
  useEffect(() => {
    if (!user?.is_super) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [adminFilter, actionFilter, search, user]);

  const clearOld = async (days) => {
    const { data } = await api.delete(`/admin/activity-logs${days ? `?older_than_days=${days}` : ""}`);
    toast.success(`Cleared ${data.deleted} ${data.deleted === 1 ? "entry" : "entries"}`);
    loadMeta();
    load();
  };

  const grouped = useMemo(() => logs, [logs]);

  return (
    <div data-testid="admin-activity">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl text-[var(--bronze)] flex items-center gap-3"><ScrollText className="w-8 h-8 text-[var(--olive)]" strokeWidth={1.3} /> Activity Log</h1>
          <p className="text-[var(--olive)] mt-1">A record of all admin actions · auto-removed after 15 days</p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="clear-logs-btn" className="flex items-center gap-2 border border-[var(--line)] px-4 py-2.5 text-sm text-[var(--bronze)] hover:border-[var(--maroon)] hover:text-[var(--maroon)] transition-colors">
              <Trash2 className="w-4 h-4" /> Clear old logs
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#FBF7DE] border-[var(--line)]">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl text-[var(--bronze)]">Clear activity logs</AlertDialogTitle>
              <AlertDialogDescription className="text-[var(--olive)]">Choose how much history to remove. This cannot be undone. Entries older than 15 days are cleared automatically regardless.</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={() => clearOld(7)} data-testid="clear-older-7" className="border border-[var(--line)] px-4 py-3 text-sm text-[var(--bronze)] hover:bg-[#F3EECB] transition-colors text-left">Clear entries older than 7 days</button>
              <button onClick={() => clearOld(1)} data-testid="clear-older-1" className="border border-[var(--line)] px-4 py-3 text-sm text-[var(--bronze)] hover:bg-[#F3EECB] transition-colors text-left">Clear entries older than 1 day</button>
              <button onClick={() => clearOld(0)} data-testid="clear-all-logs" className="border border-[var(--maroon)] px-4 py-3 text-sm text-[var(--maroon)] hover:bg-[var(--maroon)] hover:text-[#FBF7DE] transition-colors text-left">Clear ALL entries</button>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[var(--line)] text-[var(--bronze)]">Cancel</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={adminFilter} onValueChange={setAdminFilter}>
          <SelectTrigger data-testid="filter-admin" className="w-56 border-[var(--line)] bg-transparent text-[var(--bronze)]"><SelectValue placeholder="All admins" /></SelectTrigger>
          <SelectContent className="bg-[#FBF7DE] border-[var(--line)]">
            <SelectItem value="all">All admins</SelectItem>
            {meta.admins.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger data-testid="filter-action" className="w-56 border-[var(--line)] bg-transparent text-[var(--bronze)]"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent className="bg-[#FBF7DE] border-[var(--line)]">
            <SelectItem value="all">All actions</SelectItem>
            {meta.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center border border-[var(--line)] px-3 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[var(--olive)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item or admin name" data-testid="activity-search"
            className="bg-transparent px-2 py-2.5 text-sm flex-1 outline-none text-[var(--bronze)] placeholder:text-[var(--olive)]/60" />
        </div>
        <button onClick={load} className="border border-[var(--line)] p-2.5 text-[var(--olive)] hover:text-[var(--bronze)] transition-colors" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="border border-[var(--line)]">
        {loading ? (
          <div className="py-20 text-center uplabel text-[var(--olive)]">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="py-20 text-center text-[var(--olive)]">No activity recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F3EECB]"><tr className="uplabel text-[0.6rem] text-left">
              <th className="p-4">Admin</th><th className="p-4">Action</th><th className="p-4">Item</th><th className="p-4 text-right">When</th>
            </tr></thead>
            <tbody>
              {grouped.map((l) => (
                <tr key={l.id} data-testid={`activity-row-${l.id}`} className="border-t border-[var(--line)]">
                  <td className="p-4"><div className="text-[var(--bronze)]">{l.admin_name}</div><div className="text-xs text-[var(--olive)]">{l.admin_email}</div></td>
                  <td className="p-4"><span className={`uplabel text-[0.58rem] px-2 py-1 ${actionTone(l.action)}`}>{l.action}</span></td>
                  <td className="p-4 text-[var(--bronze)]">{l.item_name || "—"}</td>
                  <td className="p-4 text-right text-[var(--olive)]" title={new Date(l.created_at).toLocaleString()}>{relativeTime(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
